import { AlertTriangle, GitCompare } from 'lucide-react'
import { relativeTime } from '../lib/relativeTime'
import type { Book } from '../types'
import './MirrorFidelityPanel.css'

interface MirrorFidelityPanelProps {
  book: Book
}

// Non-string entries are filtered defensively for shape-safety only — the
// one producer (SubscriberFeed.tick, src/engine/subscriber_feed.py) always
// emits sorted string arrays, so this never actually drops anything live.
function strList(v: unknown): string[] | null {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string').sort() : null
}

function ChipGroup({
  label,
  tone,
  symbols,
}: {
  label: string
  tone: 'synced' | 'entry' | 'exit'
  symbols: string[]
}) {
  return (
    <div className="mirror-fidelity-group">
      <div className="mirror-fidelity-group-header">
        <span className={`mirror-fidelity-group-label mirror-fidelity-group-label--${tone}`}>{label}</span>
        <span className="num mirror-fidelity-group-count">{symbols.length}</span>
      </div>
      {symbols.length === 0 ? (
        <span className="caption mirror-fidelity-group-none">none</span>
      ) : (
        <div className="mirror-fidelity-chips">
          {symbols.map((sym) => (
            <span key={sym} className={`mirror-fidelity-chip mirror-fidelity-chip--${tone}`}>
              {sym}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Set-diff view of synced / pending-entry / pending-exit symbols for this
 * validation book's mirror. The source is the engine's in-memory subscriber
 * tick report (`book.bookStatus.last_conviction_tick`, broker-derived), NOT
 * the conviction scorecard: a subscriber book's SubscriberFeed never writes
 * ConvictionCandidate rows for its own account (it only ever reads the
 * source account's), so the scorecard is permanently empty here and cannot
 * be used to compute this diff.
 *
 * Three states, deliberately distinguished so a failed/stale tick can never
 * read as a healthy one:
 *  - tick === null: never ticked since the last engine restart (the report
 *    is in-memory) — "awaiting" state, no counts rendered.
 *  - tick.stale === true / tick.error present: SubscriberFeed.tick's
 *    fail-static path (src/engine/subscriber_feed.py ~86-99) retains the
 *    previous intent sets and re-raises on failure, so holds/entries/exits
 *    are still real arrays here — a distinct "failed" state surfaces the
 *    error instead of silently rendering those stale sets as current.
 *  - otherwise: healthy chip groups, with the tick's as_of age shown so a
 *    long-untouched-but-not-erroring set doesn't read as fresh either.
 */
export function MirrorFidelityPanel({ book }: MirrorFidelityPanelProps) {
  const sourceId = book.pick_source_account_id

  if (sourceId === null) {
    return (
      <div className="card mirror-fidelity-panel">
        <div className="card-header">
          <h3 className="card-title">
            <GitCompare size={18} className="icon" aria-hidden="true" />
            Mirror fidelity
          </h3>
        </div>
        <div className="empty-state">
          <div className="empty-icon"><GitCompare size={28} aria-hidden="true" /></div>
          <div className="empty-text">no pick source configured</div>
        </div>
      </div>
    )
  }

  const tick = book.bookStatus?.last_conviction_tick ?? null
  const failed = tick !== null && (tick.stale === true || 'error' in tick)
  const errorMessage = failed && typeof tick?.error === 'string' ? tick.error : null
  const asOf = tick !== null && typeof tick.as_of === 'string' ? tick.as_of : null

  const synced = strList(tick?.holds)
  const pendingEntry = strList(tick?.entries)
  const pendingExit = strList(tick?.exits)
  const awaiting = tick === null || (!failed && synced === null && pendingEntry === null && pendingExit === null)

  return (
    <div className="card mirror-fidelity-panel">
      <div className="card-header">
        <h3 className="card-title">
          <GitCompare size={18} className="icon" aria-hidden="true" />
          Mirror fidelity
        </h3>
      </div>

      {failed ? (
        <div className="empty-state mirror-fidelity-failed">
          <div className="empty-icon"><AlertTriangle size={28} aria-hidden="true" /></div>
          <div className="empty-text">
            <span className="card-badge error">last mirror tick failed</span>
          </div>
          {errorMessage && (
            <p className="caption mirror-fidelity-caption mirror-fidelity-error-detail mono">{errorMessage}</p>
          )}
          <p className="caption mirror-fidelity-caption">
            showing last-known intent from {relativeTime(asOf, 'unknown time')} — the engine retries automatically
          </p>
        </div>
      ) : awaiting ? (
        <div className="empty-state mirror-fidelity-awaiting">
          <div className="empty-icon"><GitCompare size={28} aria-hidden="true" /></div>
          <div className="empty-text">awaiting first mirror tick since restart</div>
          <p className="caption mirror-fidelity-caption">
            the tick report is in-memory and clears on every engine restart; it repopulates on the next daily tick
          </p>
        </div>
      ) : (
        <>
          <div className="mirror-fidelity-groups">
            <ChipGroup label="Synced" tone="synced" symbols={synced ?? []} />
            <ChipGroup label="Pending entry" tone="entry" symbols={pendingEntry ?? []} />
            <ChipGroup label="Pending exit" tone="exit" symbols={pendingExit ?? []} />
          </div>
          <p className="caption mirror-fidelity-caption">
            the subscriber book mirrors the research HELD set; exits mirror too · last tick {relativeTime(asOf, 'unknown time')}
          </p>
        </>
      )}
    </div>
  )
}
